/**
 * Adoption Analytics API client hooks using TanStack Query
 * Provides hooks for adoption metrics and user activity data
 */
import { useQuery } from '@tanstack/react-query'
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  AdoptionSummaryResponse,
  UsersOverTimeResponse,
  SessionsByModeResponse,
  SessionOutcomesResponse,
  TriggerSourcesResponse,
  TopUsersResponse,
  AdoptionParams,
} from '@/types/adoption'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for adoption analytics
 */
export const adoptionKeys = {
  all: ['adoption'] as const,
  summary: (from?: string, to?: string) =>
    ['adoption', 'summary', from, to] as const,
  usersOverTime: (from?: string, to?: string) =>
    ['adoption', 'usersOverTime', from, to] as const,
  sessionsByMode: (from?: string, to?: string) =>
    ['adoption', 'sessionsByMode', from, to] as const,
  sessionOutcomes: (from?: string, to?: string) =>
    ['adoption', 'sessionOutcomes', from, to] as const,
  triggerSources: (from?: string, to?: string) =>
    ['adoption', 'triggerSources', from, to] as const,
  topUsers: (from?: string, to?: string) =>
    ['adoption', 'topUsers', from, to] as const,
}

// ============================================================================
// Fetch Functions
// ============================================================================

/**
 * Fetch adoption summary statistics
 */
async function fetchAdoptionSummary(params: AdoptionParams = {}): Promise<AdoptionSummaryResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/analytics/adoption/summary', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch adoption summary',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Fetch users over time data
 */
async function fetchUsersOverTime(params: AdoptionParams = {}): Promise<UsersOverTimeResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/analytics/adoption/users-over-time', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch users over time',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Fetch sessions by mode distribution
 */
async function fetchSessionsByMode(params: AdoptionParams = {}): Promise<SessionsByModeResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/analytics/adoption/sessions-by-mode', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch sessions by mode',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Fetch session outcomes distribution
 */
async function fetchSessionOutcomes(params: AdoptionParams = {}): Promise<SessionOutcomesResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/analytics/adoption/session-outcomes', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch session outcomes',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Fetch trigger sources distribution
 */
async function fetchTriggerSources(params: AdoptionParams = {}): Promise<TriggerSourcesResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/analytics/adoption/trigger-sources', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch trigger sources',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Fetch top users by activity
 */
async function fetchTopUsers(params: AdoptionParams = {}): Promise<TopUsersResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/analytics/adoption/top-users', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch top users',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

// ============================================================================
// React Query Hooks
// ============================================================================

interface QueryOptions {
  enabled?: boolean
  refetchInterval?: number
}

/**
 * Hook to fetch adoption summary statistics
 * 
 * @param params - Date range parameters
 * @param options - Optional query options
 * @returns Query result with adoption summary
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useAdoptionSummary({ from: '2026-01-01', to: '2026-02-14' })
 * ```
 */
export function useAdoptionSummary(
  params: AdoptionParams = {},
  options?: QueryOptions
) {
  return useQuery({
    queryKey: adoptionKeys.summary(params.from, params.to),
    queryFn: () => fetchAdoptionSummary(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000, // Consider stats fresh for 1 minute
  })
}

/**
 * Hook to fetch users over time data
 * 
 * @param params - Date range parameters
 * @param options - Optional query options
 * @returns Query result with users over time data
 */
export function useUsersOverTime(
  params: AdoptionParams = {},
  options?: QueryOptions
) {
  return useQuery({
    queryKey: adoptionKeys.usersOverTime(params.from, params.to),
    queryFn: () => fetchUsersOverTime(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000,
  })
}

/**
 * Hook to fetch sessions by mode distribution
 * 
 * @param params - Date range parameters
 * @param options - Optional query options
 * @returns Query result with sessions by mode data
 */
export function useSessionsByMode(
  params: AdoptionParams = {},
  options?: QueryOptions
) {
  return useQuery({
    queryKey: adoptionKeys.sessionsByMode(params.from, params.to),
    queryFn: () => fetchSessionsByMode(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000,
  })
}

/**
 * Hook to fetch session outcomes distribution
 * 
 * @param params - Date range parameters
 * @param options - Optional query options
 * @returns Query result with session outcomes data
 */
export function useSessionOutcomes(
  params: AdoptionParams = {},
  options?: QueryOptions
) {
  return useQuery({
    queryKey: adoptionKeys.sessionOutcomes(params.from, params.to),
    queryFn: () => fetchSessionOutcomes(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000,
  })
}

/**
 * Hook to fetch trigger sources distribution
 * 
 * @param params - Date range parameters
 * @param options - Optional query options
 * @returns Query result with trigger sources data
 */
export function useTriggerSources(
  params: AdoptionParams = {},
  options?: QueryOptions
) {
  return useQuery({
    queryKey: adoptionKeys.triggerSources(params.from, params.to),
    queryFn: () => fetchTriggerSources(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000,
  })
}

/**
 * Hook to fetch top users by activity
 * 
 * @param params - Date range parameters
 * @param options - Optional query options
 * @returns Query result with top users data
 */
export function useTopUsers(
  params: AdoptionParams = {},
  options?: QueryOptions
) {
  return useQuery({
    queryKey: adoptionKeys.topUsers(params.from, params.to),
    queryFn: () => fetchTopUsers(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000,
  })
}
